'use client';

import Tracker from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';

import { env } from '../env';

let tracker: Tracker | null = null;

/**
 * Initialize OpenReplay tracker
 * This should be called once in the app, typically in a root layout or provider
 */
export function initOpenReplay(): Tracker | null {
  // Skip initialization if already initialized or if config is missing
  if (tracker || typeof window === 'undefined') {
    return tracker;
  }

  const projectKey = env.NEXT_PUBLIC_OPENREPLAY_PROJECT_KEY;
  const ingestPoint = env.NEXT_PUBLIC_OPENREPLAY_INGEST_POINT;

  // Skip if OpenReplay is not configured
  if (!projectKey) {
    console.warn('OpenReplay project key not configured');
    return null;
  }

  try {
    tracker = new Tracker({
      projectKey,
      // Use OpenReplay cloud ingest point if not specified
      ingestPoint: ingestPoint || 'https://api.openreplay.com/ingest',
      __DISABLE_SECURE_MODE: process.env.NODE_ENV === 'development'
    });

    // Use the assist plugin for live support features
    tracker.use(trackerAssist());

    tracker.start();

    console.info('OpenReplay initialized successfully');
  } catch (error) {
    console.error('Failed to initialize OpenReplay:', error);
    tracker = null;
  }

  return tracker;
}

/**
 * Get the current tracker instance
 */
export function getTracker(): Tracker | null {
  return tracker;
}

/**
 * Track a custom event in OpenReplay
 */
export function trackEvent(
  eventName: string,
  payload?: Record<string, unknown>
): void {
  if (!tracker) {
    return;
  }

  try {
    tracker.event(eventName, payload);
  } catch (error) {
    console.error('Failed to track OpenReplay event:', error);
  }
}

/**
 * Set user metadata for the current session
 */
export function setUserMetadata(metadata: {
  userId?: string;
  email?: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
}): void {
  if (!tracker) {
    return;
  }

  try {
    if (metadata.userId) {
      tracker.setUserID(metadata.userId);
    }

    // Set metadata fields
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined && key !== 'userId') {
        tracker?.setMetadata(key, String(value));
      }
    });
  } catch (error) {
    console.error('Failed to set OpenReplay user metadata:', error);
  }
}
